import { useState, useEffect, useCallback, useRef } from 'react';
import type { Lead, LeadGenProgress } from '../../types';
import { apiClient } from '../../lib/api';
import { TABLE_TIMINGS } from '../../constants/table';

/**
 * Format EXA webset items as leads for the frontend (interim results)
 */
const formatInterimItemsAsLeads = (items: any[]): any[] => {
  return items.map((item, index) => {
    try {
      const props = item.properties || {};
      let name = '';
      let url = '';
      let description = '';

      // Extract name based on entity type
      if (props.company && props.company.name) {
        name = props.company.name;
      } else if (props.person && props.person.name) {
        name = props.person.name;
      } else if (props.article && props.article.title) {
        name = props.article.title;
      } else if (props.title) {
        name = props.title;
      } else if (props.name) {
        name = props.name;
      }

      // Extract URL
      if (props.url) {
        url = props.url;
      } else if (props.company && props.company.url) {
        url = props.company.url;
      }

      // Extract description
      if (props.description) {
        description = props.description;
      } else if (props.company && props.company.about) {
        description = props.company.about;
      } else if (props.person && props.person.bio) {
        description = props.person.bio;
      }

      return {
        id: `interim_${index}`,
        data: {
          Name: name || 'Unknown',
          URL: url || '',
          Description: (description || '').slice(0, 200)
        },
        reasoning: {},
        metadata: {
          ...props,
          is_interim: true
        },
        is_interim: true
      };
    } catch (error) {
      console.debug('Error formatting interim item:', error);
      return {
        id: `interim_error_${index}`,
        data: {
          Name: 'Error loading item',
          URL: '',
          Description: ''
        },
        reasoning: {},
        metadata: { is_interim: true },
        is_interim: true
      };
    }
  });
};

export interface UseTableProgressProps {
  tableId: string;
  tableStatus?: string;
  leads: Lead[];
  onProgressUpdate?: (data: LeadGenProgress | null) => void;
}

export interface UseTableProgressReturn {
  progressData: LeadGenProgress | null;
  interimLeads: Lead[];
  isLoadingRows: boolean;
  fetchProgressData: () => Promise<void>;
}

/**
 * Hook for managing lead generation progress tracking
 */
export const useTableProgress = ({
  tableId,
  tableStatus,
  leads,
  onProgressUpdate,
}: UseTableProgressProps): UseTableProgressReturn => {
  const [progressData, setProgressData] = useState<LeadGenProgress | null>(null);
  const [interimLeads, setInterimLeads] = useState<Lead[]>([]);

  const progressDataFetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressDataCallInProgress = useRef<boolean>(false);
  const isMountedRef = useRef(true);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressDataFetchTimeoutRef.current) {
        clearTimeout(progressDataFetchTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Fetch progress data from API
   */
  const fetchProgressData = useCallback(async () => {
    try {
      if (progressDataCallInProgress.current) {
        return;
      }
      progressDataCallInProgress.current = true;
      const data = await apiClient.getDatasetStatus(tableId);

      if (!isMountedRef.current) return;

      setProgressData(data);

      if (progressDataFetchTimeoutRef.current) {
        clearTimeout(progressDataFetchTimeoutRef.current);
      }

      if (data.status === 'in_progress') {
        progressDataFetchTimeoutRef.current = setTimeout(
          fetchProgressData,
          TABLE_TIMINGS.PROGRESS_POLL_INTERVAL
        );
      }
    } catch (e) {
      console.debug('Could not fetch progress data:', e);
    } finally {
      progressDataCallInProgress.current = false;
    }
  }, [tableId]);

  // Start/stop progress polling based on table status
  useEffect(() => {
    if (tableStatus === 'generating') {
      setTimeout(() => fetchProgressData(), TABLE_TIMINGS.INITIAL_PROGRESS_DELAY);
    } else {
      setProgressData(null);
      setInterimLeads([]);
      if (progressDataFetchTimeoutRef.current) {
        clearTimeout(progressDataFetchTimeoutRef.current);
        progressDataFetchTimeoutRef.current = null;
      }
    }
  }, [tableStatus, tableId, fetchProgressData]);

  // Fetch interim leads when progress shows more found
  useEffect(() => {
    const fetchInterimLeads = async () => {
      try {
        const interimData = await apiClient.getInterimItems(tableId);
        const formattedInterimLeads = interimData.items?.length > 0
          ? formatInterimItemsAsLeads(interimData.items)
          : [];

        if (isMountedRef.current) {
          setInterimLeads(formattedInterimLeads as Lead[]);
        }
      } catch (error) {
        console.debug('Could not fetch interim items:', error);
        if (isMountedRef.current) {
          setInterimLeads([]);
        }
      }
    };

    if (progressData && progressData.found > leads.length) {
      fetchInterimLeads();
    }
  }, [progressData, leads, tableId]);

  // Notify parent of progress updates
  useEffect(() => {
    onProgressUpdate?.(progressData);
  }, [progressData, onProgressUpdate]);

  // Calculate loading state
  const isLoadingRows = (() => {
    const isProcessing = tableStatus === 'generating' ||
                        (!tableStatus && leads.length === 0);
    const isCompleted = tableStatus === 'completed' ||
                       tableStatus === 'completed_no_results' ||
                       tableStatus?.startsWith('error');

    return !!(leads.length === 0 && interimLeads.length === 0 && tableId && isProcessing && !isCompleted);
  })();

  return {
    progressData,
    interimLeads,
    isLoadingRows,
    fetchProgressData,
  };
};
